import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    ChevronLeft, ChevronRight, Plus, X, Clock, MapPin, Calendar as CalendarIcon,
    RefreshCw, Edit3, Trash2, Save, Users, Eye, EyeOff, Settings, Mail, Repeat, Search
} from 'lucide-react';
import {
    getEvents, createEvent, updateEvent, deleteEvent,
    generateAvailabilityEvents, EVENT_TYPES, RECURRENCE_OPTIONS,
    parseRecurrenceRule, getRecurrenceEndDate,
    getAgendaParticipants, addAgendaParticipant, removeAgendaParticipant,
    getAgendaGroups, addAgendaGroup, removeAgendaGroup
} from '../../services/agendaService';
import { searchParticipantsAndGroups } from '../../services/messagesService';
// TODO: Re-enable Google Calendar integration
// import {
//     getConnectionStatus,
//     getGoogleEvents
// } from '../../services/googleCalendarService';
import { useTranslation } from '../../context/LanguageContext';
import CustomDropdown from '../CustomDropdown';
import CalendarSettings from './CalendarSettings';
import AvailabilityModal from './AvailabilityModal';

/**
 * Full Calendar Component - Google Calendar style
 */
const Calendar = () => {
    const { t, language } = useTranslation();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [view, setView] = useState('week'); // 'month', 'week', 'day'
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showEventModal, setShowEventModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'

    // Drag & Drop state
    const [draggedEvent, setDraggedEvent] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [resizeEdge, setResizeEdge] = useState(null); // 'top' or 'bottom'
    const [dragStartY, setDragStartY] = useState(null);
    const [dragStartX, setDragStartX] = useState(null);
    const [dragStartDate, setDragStartDate] = useState(null);
    const [pendingDrag, setPendingDrag] = useState(null); // { event, date } - waiting for mouse move to confirm drag
    const [pendingResize, setPendingResize] = useState(null); // { event, edge } - waiting for mouse move to confirm resize
    const [dragMousePos, setDragMousePos] = useState(null); // { x, y } - current mouse position during drag
    const justFinishedDragRef = useRef(false);
    const DRAG_THRESHOLD = 5; // minimum pixels to move before drag starts

    // Slot selection state (for creating new events by dragging)
    const [slotSelection, setSlotSelection] = useState(null); // { date, startMinutes, endMinutes }

    // Google Calendar state - DISABLED
    const [showSettings, setShowSettings] = useState(false);
    const [showNewEventMenu, setShowNewEventMenu] = useState(false);
    const newEventMenuRef = useRef(null);
    const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
    const [activeFilters, setActiveFilters] = useState({
        events: true,
        meetings: true,
        appointments: true,
        availability: true,
        google: false // Disabled
    });
    // const [googleConnection, setGoogleConnection] = useState({ isConnected: false, isActive: false });
    // const [googleEvents, setGoogleEvents] = useState([]);
    const googleConnection = { isConnected: false, isActive: false };
    const googleEvents = [];

    const userIdStr = localStorage.getItem('userId');
    const userId = userIdStr ? parseInt(userIdStr, 10) : null;

    // Load events when date range changes
    useEffect(() => {
        loadEvents();
    }, [currentDate, view]);

    // Google Calendar - DISABLED
    // useEffect(() => {
    //     if (userId) {
    //         loadGoogleConnectionStatus();
    //     }
    // }, [userId]);

    // useEffect(() => {
    //     if (googleConnection.isConnected && googleConnection.isActive && userId) {
    //         loadGoogleEvents();
    //     }
    // }, [googleConnection.isConnected, googleConnection.isActive, currentDate, view]);

    // const loadGoogleConnectionStatus = async () => {
    //     const status = await getConnectionStatus(userId);
    //     setGoogleConnection(status);
    // };

    // const loadGoogleEvents = async () => {
    //     const { startDate, endDate } = getDateRange();
    //     const events = await getGoogleEvents(userId, startDate.toISOString(), endDate.toISOString());
    //     setGoogleEvents(events);
    // };

    const handleGoogleConnectionChange = (isConnected) => {
        // Disabled
    };

    // Change cursor to grabbing when mouse is down on event or during drag
    useEffect(() => {
        if (isDragging || pendingDrag || pendingResize) {
            document.body.classList.add('cursor-grabbing-global');
            return () => {
                document.body.classList.remove('cursor-grabbing-global');
            };
        }
    }, [isDragging, pendingDrag, pendingResize]);

    // Close new event menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (newEventMenuRef.current && !newEventMenuRef.current.contains(event.target)) {
                setShowNewEventMenu(false);
            }
        };
        if (showNewEventMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showNewEventMenu]);

    const toggleFilter = (filterName) => {
        setActiveFilters(prev => ({
            ...prev,
            [filterName]: !prev[filterName]
        }));
    };

    // Merge and filter events based on active filters (multiple can be active)
    const filteredEvents = useMemo(() => {
        let result = [];

        // Add local events based on filters
        if (activeFilters.events) {
            result = [...result, ...events.filter(e =>
                e.eventType === 'event' || !e.eventType
            )];
        }

        if (activeFilters.meetings) {
            result = [...result, ...events.filter(e => e.eventType === 'meeting')];
        }

        if (activeFilters.appointments) {
            result = [...result, ...events.filter(e => e.eventType === 'appointment')];
        }

        if (activeFilters.availability) {
            result = [...result, ...events.filter(e => e.eventType === 'availability')];
        }

        // Add Google events if connected and filter is active
        if (activeFilters.google && googleConnection.isConnected && googleConnection.isActive) {
            result = [...result, ...googleEvents];
        }

        return result;
    }, [events, googleEvents, activeFilters, googleConnection.isConnected, googleConnection.isActive]);

    const loadEvents = async () => {
        if (!userId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const { startDate, endDate } = getDateRange();
            const data = await getEvents(userId, {
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString()
            });
            setEvents(data || []);
        } catch (err) {
            console.error('Error loading events:', err);
            setEvents([]); // Set empty array on error so calendar shows
        } finally {
            setLoading(false);
        }
    };

    const getDateRange = () => {
        const start = new Date(currentDate);
        const end = new Date(currentDate);

        if (view === 'month') {
            start.setDate(1);
            start.setDate(start.getDate() - start.getDay());
            end.setMonth(end.getMonth() + 1);
            end.setDate(0);
            end.setDate(end.getDate() + (6 - end.getDay()));
        } else if (view === 'week') {
            // Calculate Monday of the current week
            const dayOfWeek = start.getDay(); // 0 = Sunday, 1 = Monday, etc.
            const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            start.setDate(start.getDate() + diffToMonday);
            // End is Sunday (6 days after Monday)
            end.setTime(start.getTime());
            end.setDate(end.getDate() + 6);
        } else {
            // day view - just that day
        }

        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        return { startDate: start, endDate: end };
    };

    const navigatePrevious = () => {
        const newDate = new Date(currentDate);
        if (view === 'month') {
            newDate.setMonth(newDate.getMonth() - 1);
        } else if (view === 'week') {
            newDate.setDate(newDate.getDate() - 7);
        } else {
            newDate.setDate(newDate.getDate() - 1);
        }
        setCurrentDate(newDate);
    };

    const navigateNext = () => {
        const newDate = new Date(currentDate);
        if (view === 'month') {
            newDate.setMonth(newDate.getMonth() + 1);
        } else if (view === 'week') {
            newDate.setDate(newDate.getDate() + 7);
        } else {
            newDate.setDate(newDate.getDate() + 1);
        }
        setCurrentDate(newDate);
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const handleCreateEvent = (date, hour = null, endHour = null, startMinutes = 0, endMinutes = 0, eventType = null) => {
        // Block if we just finished a drag/resize
        if (justFinishedDragRef.current) {
            console.log('Blocking create event - just finished drag/resize');
            return;
        }
        // Block if currently dragging or resizing
        if (isDragging || isResizing) {
            console.log('Blocking create event - currently dragging/resizing');
            return;
        }

        const startDate = new Date(date);
        if (hour !== null) {
            startDate.setHours(hour, startMinutes, 0, 0);
        } else {
            startDate.setHours(9, 0, 0, 0);
        }
        const endDate = new Date(date);
        if (endHour !== null) {
            endDate.setHours(endHour, endMinutes, 0, 0);
        } else {
            endDate.setHours(startDate.getHours() + 1, startDate.getMinutes(), 0, 0);
        }

        // Helper to format date to local ISO string
        const toLocalISO = (d) => {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        // Get event type details
        const selectedType = eventType ? EVENT_TYPES.find(t => t.value === eventType) : EVENT_TYPES.find(t => t.value === 'appointment');
        const typeColor = selectedType?.color || '#8b5cf6';

        setSelectedEvent({
            title: '',
            description: '',
            location: '',
            start: toLocalISO(startDate),
            end: toLocalISO(endDate),
            allDay: false,
            eventType: eventType || 'appointment',
            color: typeColor,
            isPrivate: false
        });
        setModalMode('create');
        setShowEventModal(true);
    };

    // Slot selection handlers (for creating events by dragging)
    // slotSelection format: { startDate, endDate, startMinutes, endMinutes }
    const handleSlotSelectionStart = (date, minutes) => {
        if (isDragging || isResizing) return;
        setSlotSelection({
            startDate: new Date(date),
            endDate: new Date(date),
            startMinutes: minutes,
            endMinutes: minutes + 15
        });
    };

    const handleSlotSelectionMove = (date, minutes) => {
        if (!slotSelection) return;
        const newEndDate = new Date(date);
        const startDate = slotSelection.startDate;

        // Compare dates (ignore time)
        const isSameDay = startDate.toDateString() === newEndDate.toDateString();
        const isAfterStart = newEndDate >= startDate;

        if (isSameDay) {
            // Same day - just update end minutes (ensure minimum 15 minutes)
            const endMinutes = Math.max(slotSelection.startMinutes + 15, minutes + 15);
            setSlotSelection(prev => ({ ...prev, endDate: newEndDate, endMinutes }));
        } else if (isAfterStart) {
            // Different day (after start) - update end date and minutes
            setSlotSelection(prev => ({
                ...prev,
                endDate: newEndDate,
                endMinutes: minutes + 15
            }));
        }
        // If before start date, don't update (keep original selection)
    };

    const handleSlotSelectionEnd = () => {
        if (!slotSelection) return;
        const { startDate, endDate, startMinutes, endMinutes } = slotSelection;
        const startHour = Math.floor(startMinutes / 60);
        const startMin = startMinutes % 60;
        const endHour = Math.floor(endMinutes / 60);
        const endMin = endMinutes % 60;

        // Don't clear selection - keep it visible while modal is open
        // setSlotSelection(null);

        // Create event with start and end dates
        const eventStartDate = new Date(startDate);
        eventStartDate.setHours(startHour, startMin, 0, 0);
        const eventEndDate = new Date(endDate);
        eventEndDate.setHours(endHour, endMin, 0, 0);

        handleCreateEventWithDates(eventStartDate, eventEndDate);
    };

    // New handler for creating events with full date objects
    const handleCreateEventWithDates = (startDate, endDate) => {
        const toLocalISO = (d) => {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        setSelectedEvent({
            title: '',
            description: '',
            location: '',
            start: toLocalISO(startDate),
            end: toLocalISO(endDate),
            allDay: false,
            eventType: 'appointment',
            color: '#8b5cf6',
            isPrivate: false
        });
        setModalMode('create');
        setShowEventModal(true);
    };

    const handleViewEvent = (event) => {
        // Block if we just finished a drag/resize
        if (justFinishedDragRef.current) {
            justFinishedDragRef.current = false;
            return;
        }
        setSelectedEvent({
            ...event,
            start: event.start?.slice(0, 16) || '',
            end: event.end?.slice(0, 16) || ''
        });
        setModalMode('view');
        setShowEventModal(true);
    };

    const handleEditEvent = (event) => {
        // Block if we just finished a drag/resize
        if (justFinishedDragRef.current) {
            justFinishedDragRef.current = false;
            return;
        }
        setSelectedEvent({
            ...event,
            start: event.start?.slice(0, 16) || '',
            end: event.end?.slice(0, 16) || ''
        });
        setModalMode('edit');
        setShowEventModal(true);
    };

    const handleSwitchToEdit = () => {
        setModalMode('edit');
    };

    const handleSaveEvent = async (eventData, newParticipants = []) => {
        try {
            if (modalMode === 'create') {
                const result = await createEvent({
                    ...eventData,
                    peopleId: userId,
                    startDateTime: eventData.start,
                    endDateTime: eventData.end
                });
                // Add participants to the newly created event
                if (result?.data?.id && newParticipants.length > 0) {
                    for (const participant of newParticipants) {
                        await addAgendaParticipant(result.data.id, participant.id, 'confirmed');
                    }
                }
            } else {
                await updateEvent(eventData.eventUuid, {
                    ...eventData,
                    startDateTime: eventData.start,
                    endDateTime: eventData.end
                });
                // Add new participants to existing event
                if (eventData.id && newParticipants.length > 0) {
                    for (const participant of newParticipants) {
                        await addAgendaParticipant(eventData.id, participant.id, 'confirmed');
                    }
                }
            }
            setShowEventModal(false);
            setSlotSelection(null);
            loadEvents();
        } catch (err) {
            console.error('Error saving event:', err);
            alert(t.common?.error || 'Error');
        }
    };

    const handleDeleteEvent = async (eventUuid) => {
        if (!confirm(t.calendar?.deleteConfirm || 'Delete this event?')) {
            return;
        }
        try {
            await deleteEvent(eventUuid);
            setShowEventModal(false);
            setSlotSelection(null);
            loadEvents();
        } catch (err) {
            console.error('Error deleting event:', err);
        }
    };

    const handleSaveAvailability = async (slotData) => {
        try {
            // Create the availability event
            await createEvent({
                peopleId: userId,
                title: slotData.title,
                startDateTime: slotData.startDateTime,
                endDateTime: slotData.endDateTime,
                eventType: 'availability',
                color: '#22c55e',
                isPrivate: false,
                recurrence: slotData.recurrent ? 'weekly' : 'none'
            });

            loadEvents();
        } catch (err) {
            console.error('Error saving availability:', err);
            alert(t.common?.error || 'Error');
            throw err;
        }
    };

    // Drag & Drop handlers - now with threshold detection
    const handleMouseDown = (event, e, date) => {
        e.stopPropagation();
        e.preventDefault();
        // Store pending drag info - will only activate if mouse moves beyond threshold
        setPendingDrag({ event, date });
        setDragStartY(e.clientY);
        setDragStartX(e.clientX);
    };

    const handleResizeMouseDown = (event, edge, e) => {
        e.stopPropagation();
        e.preventDefault();
        // Store pending resize info - will only activate if mouse moves beyond threshold
        setPendingResize({ event, edge });
        setDragStartY(e.clientY);
        setDragStartX(e.clientX);
    };

    const activateDrag = () => {
        if (!pendingDrag) return;
        setDraggedEvent(pendingDrag.event);
        setIsDragging(true);
        setDragStartDate(pendingDrag.date);
        setPendingDrag(null);
    };

    const activateResize = () => {
        if (!pendingResize) return;
        setDraggedEvent(pendingResize.event);
        setIsResizing(true);
        setResizeEdge(pendingResize.edge);
        setPendingResize(null);
    };

    const cancelPending = () => {
        setPendingDrag(null);
        setPendingResize(null);
        setDragStartY(null);
        setDragStartX(null);
    };

    // Helper to format date to local ISO string (YYYY-MM-DDTHH:mm)
    const toLocalISOString = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Snap minutes to nearest quarter hour (0, 15, 30, 45)
    const snapToQuarter = (minutes) => Math.round(minutes / 15) * 15;

    const handleDragEnd = async (newDate, newMinutes) => {
        if (!draggedEvent) return;

        try {
            const eventStart = new Date(draggedEvent.start);
            const eventEnd = new Date(draggedEvent.end);
            const duration = eventEnd - eventStart;

            // Snap to quarter hour
            const snappedMinutes = snapToQuarter(newMinutes);
            const newHour = Math.floor(snappedMinutes / 60);
            const newMin = snappedMinutes % 60;

            // New start time
            const newStart = new Date(newDate);
            newStart.setHours(newHour, newMin, 0, 0);
            const newEnd = new Date(newStart.getTime() + duration);

            const startStr = toLocalISOString(newStart);
            const endStr = toLocalISOString(newEnd);

            console.log('Drag end - updating event:', {
                eventUuid: draggedEvent.eventUuid,
                from: { start: draggedEvent.start, end: draggedEvent.end },
                to: { start: startStr, end: endStr }
            });

            // Optimistic update: update local state immediately
            setEvents(prevEvents => prevEvents.map(e =>
                e.id === draggedEvent.id
                    ? { ...e, start: startStr, end: endStr }
                    : e
            ));

            // Reset drag state immediately for smooth UX
            justFinishedDragRef.current = true;
            setTimeout(() => { justFinishedDragRef.current = false; }, 100);
            setDraggedEvent(null);
            setIsDragging(false);
            setDragStartY(null);
            setDragStartDate(null);

            // Update API in background (no await, no reload)
            updateEvent(draggedEvent.eventUuid, {
                ...draggedEvent,
                start: startStr,
                end: endStr
            }).then(result => {
                console.log('Update result:', result);
            }).catch(err => {
                console.error('Error updating event in API:', err);
                // Optionally reload events on error to restore correct state
                loadEvents();
            });
        } catch (err) {
            console.error('Error moving event:', err);
            justFinishedDragRef.current = true;
            setTimeout(() => { justFinishedDragRef.current = false; }, 100);
            setDraggedEvent(null);
            setIsDragging(false);
            setDragStartY(null);
            setDragStartDate(null);
        }
    };

    const handleResizeEnd = async (deltaMinutes) => {
        if (!draggedEvent || !resizeEdge) {
            return;
        }

        try {
            const eventStart = new Date(draggedEvent.start);
            const eventEnd = new Date(draggedEvent.end);

            let newStart = eventStart;
            let newEnd = eventEnd;

            // Snap deltaMinutes to quarter hour
            const snappedDelta = snapToQuarter(deltaMinutes);

            if (resizeEdge === 'top') {
                newStart = new Date(eventStart.getTime() + snappedDelta * 60000);
                // Snap to quarter hour
                newStart.setMinutes(snapToQuarter(newStart.getMinutes()) % 60);
                if (newStart >= eventEnd) {
                    newStart = new Date(eventEnd.getTime() - 15 * 60000); // Minimum 15 min
                }
            } else {
                newEnd = new Date(eventEnd.getTime() + snappedDelta * 60000);
                // Snap to quarter hour
                newEnd.setMinutes(snapToQuarter(newEnd.getMinutes()) % 60);
                if (newEnd <= eventStart) {
                    newEnd = new Date(eventStart.getTime() + 15 * 60000); // Minimum 15 min
                }
            }

            const startStr = toLocalISOString(newStart);
            const endStr = toLocalISOString(newEnd);

            // Optimistic update: update local state immediately
            setEvents(prevEvents => prevEvents.map(e =>
                e.id === draggedEvent.id
                    ? { ...e, start: startStr, end: endStr }
                    : e
            ));

            // Reset resize state immediately for smooth UX
            justFinishedDragRef.current = true;
            setTimeout(() => { justFinishedDragRef.current = false; }, 100);
            setDraggedEvent(null);
            setIsResizing(false);
            setResizeEdge(null);
            setDragStartY(null);

            // Update API in background (no await, no reload)
            updateEvent(draggedEvent.eventUuid, {
                ...draggedEvent,
                start: startStr,
                end: endStr
            }).then(result => {
                console.log('Resize update result:', result);
            }).catch(err => {
                console.error('Error updating event in API:', err);
                // Reload events on error to restore correct state
                loadEvents();
            });
        } catch (err) {
            console.error('Error resizing event:', err);
            justFinishedDragRef.current = true;
            setTimeout(() => { justFinishedDragRef.current = false; }, 100);
            setDraggedEvent(null);
            setIsResizing(false);
            setResizeEdge(null);
            setDragStartY(null);
        }
    };

    const resetDragState = () => {
        setDraggedEvent(null);
        setIsDragging(false);
        setIsResizing(false);
        setResizeEdge(null);
        setDragStartY(null);
        setDragStartX(null);
        setDragStartDate(null);
        setPendingDrag(null);
        setPendingResize(null);
        setDragMousePos(null);
    };

    // Format header based on view
    const getHeaderTitle = () => {
        const options = { month: 'long', year: 'numeric' };
        if (view === 'month') {
            return currentDate.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', options);
        } else if (view === 'week') {
            const { startDate, endDate } = getDateRange();
            const startMonth = startDate.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short' });
            const endMonth = endDate.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short' });
            if (startMonth === endMonth) {
                return `${startDate.getDate()} - ${endDate.getDate()} ${startMonth} ${startDate.getFullYear()}`;
            }
            return `${startDate.getDate()} ${startMonth} - ${endDate.getDate()} ${endMonth} ${startDate.getFullYear()}`;
        } else {
            return currentDate.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={goToToday}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        {t.calendar?.today || 'Today'}
                    </button>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={navigatePrevious}
                            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={navigateNext}
                            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                    <h2 className="text-lg font-semibold text-gray-800 capitalize">
                        {getHeaderTitle()}
                    </h2>
                </div>

                <div className="flex items-center gap-2">
                    {/* View selector */}
                    <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                        {['month', 'week', 'day'].map((v) => (
                            <button
                                key={v}
                                onClick={() => setView(v)}
                                className={`px-3 py-1.5 text-sm transition-colors ${
                                    view === v
                                        ? 'bg-linkoo-purple text-white'
                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                {v === 'month' && (t.calendar?.month || 'Month')}
                                {v === 'week' && (t.calendar?.week || 'Week')}
                                {v === 'day' && (t.calendar?.day || 'Day')}
                            </button>
                        ))}
                    </div>

                    {/* Configure availability button */}
                    <button
                        onClick={() => setShowAvailabilityModal(true)}
                        className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                        title={t.calendar?.configureAvailability || 'Configure availability'}
                    >
                        <Clock className="w-4 h-4" />
                    </button>

                    {/* Add event button with dropdown */}
                    <div className="relative" ref={newEventMenuRef}>
                        <button
                            onClick={() => setShowNewEventMenu(!showNewEventMenu)}
                            className="flex items-center gap-2 px-4 py-1.5 bg-linkoo-purple text-white rounded-lg hover:bg-linkoo-pink transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            <span className="hidden sm:inline">
                                {t.calendar?.newEvent || 'New Event'}
                            </span>
                            <ChevronRight className={`w-4 h-4 transition-transform ${showNewEventMenu ? 'rotate-90' : ''}`} />
                        </button>

                        {/* Dropdown menu */}
                        {showNewEventMenu && (
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="px-3 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
                                    {t.calendar?.selectEventType || 'Choisir le type'}
                                </div>
                                {EVENT_TYPES.filter(type => type.value !== 'event').map((type) => (
                                    <button
                                        key={type.value}
                                        onClick={() => {
                                            handleCreateEvent(currentDate, null, null, 0, 0, type.value);
                                            setShowNewEventMenu(false);
                                        }}
                                        className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                                    >
                                        <div
                                            className="w-4 h-4 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: type.color }}
                                        />
                                        <span className="text-sm font-medium text-gray-700">
                                            {language === 'fr' ? type.labelFr : type.labelEn}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Settings button - DISABLED (Google Calendar not active)
                    <button
                        onClick={() => setShowSettings(true)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title={t.calendar?.settings || 'Settings'}
                    >
                        <Settings className="w-5 h-5 text-gray-600" />
                    </button>
                    */}
                </div>
            </div>

            {/* Filter badges (toggle) */}
            <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-2 overflow-x-auto">
                <span className="text-xs text-gray-400 mr-1">{t.calendar?.show || 'Afficher'} :</span>
                <button
                    onClick={() => toggleFilter('events')}
                    className={`px-3 py-1 text-sm rounded-full transition-all whitespace-nowrap flex items-center gap-1.5 ${
                        activeFilters.events
                            ? 'bg-blue-500 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                >
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {t.calendar?.filterEvents || 'Événements'}
                </button>
                <button
                    onClick={() => toggleFilter('meetings')}
                    className={`px-3 py-1 text-sm rounded-full transition-all whitespace-nowrap flex items-center gap-1.5 ${
                        activeFilters.meetings
                            ? 'bg-amber-500 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                >
                    <Users className="w-3.5 h-3.5" />
                    {t.calendar?.filterMeetings || 'Réunions'}
                </button>
                <button
                    onClick={() => toggleFilter('appointments')}
                    className={`px-3 py-1 text-sm rounded-full transition-all whitespace-nowrap flex items-center gap-1.5 ${
                        activeFilters.appointments
                            ? 'bg-violet-500 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                >
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {t.calendar?.filterAppointments || 'Rendez-vous'}
                </button>
                <button
                    onClick={() => toggleFilter('availability')}
                    className={`px-3 py-1 text-sm rounded-full transition-all whitespace-nowrap flex items-center gap-1.5 ${
                        activeFilters.availability
                            ? 'bg-green-500 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                >
                    <Clock className="w-3.5 h-3.5" />
                    {t.calendar?.filterAvailability || 'Disponibilités'}
                </button>
                {/* Google Agenda badge - DISABLED
                {googleConnection.isConnected && googleConnection.isActive && (
                    <button
                        onClick={() => toggleFilter('google')}
                        className={`px-3 py-1 text-sm rounded-full transition-all whitespace-nowrap flex items-center gap-1.5 ${
                            activeFilters.google
                                ? 'bg-red-500 text-white shadow-sm'
                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                    >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Google Agenda
                    </button>
                )}
                */}
            </div>

            {/* Calendar Grid */}
            <div className="flex-1 overflow-auto">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <RefreshCw className="w-8 h-8 text-linkoo-purple animate-spin" />
                    </div>
                ) : view === 'month' ? (
                    <MonthView
                        currentDate={currentDate}
                        events={filteredEvents}
                        language={language}
                        t={t}
                        onEventClick={handleViewEvent}
                        onEventDoubleClick={handleEditEvent}
                        onDateClick={handleCreateEvent}
                    />
                ) : view === 'week' ? (
                    <WeekView
                        currentDate={currentDate}
                        events={filteredEvents}
                        language={language}
                        t={t}
                        onEventClick={handleViewEvent}
                        onEventDoubleClick={handleEditEvent}
                        onSlotClick={handleCreateEvent}
                        isDragging={isDragging}
                        isResizing={isResizing}
                        draggedEvent={draggedEvent}
                        pendingDrag={pendingDrag}
                        pendingResize={pendingResize}
                        onMouseDown={handleMouseDown}
                        onResizeMouseDown={handleResizeMouseDown}
                        onDragEnd={handleDragEnd}
                        onResizeEnd={handleResizeEnd}
                        dragStartY={dragStartY}
                        dragStartX={dragStartX}
                        dragThreshold={DRAG_THRESHOLD}
                        activateDrag={activateDrag}
                        activateResize={activateResize}
                        cancelPending={cancelPending}
                        resetDragState={resetDragState}
                        resizeEdge={resizeEdge}
                        slotSelection={slotSelection}
                        onSlotSelectionStart={handleSlotSelectionStart}
                        onSlotSelectionMove={handleSlotSelectionMove}
                        onSlotSelectionEnd={handleSlotSelectionEnd}
                        dragMousePos={dragMousePos}
                        setDragMousePos={setDragMousePos}
                        isModalOpen={showEventModal}
                    />
                ) : (
                    <DayView
                        currentDate={currentDate}
                        events={filteredEvents}
                        language={language}
                        t={t}
                        onEventClick={handleViewEvent}
                        onEventDoubleClick={handleEditEvent}
                        onSlotClick={handleCreateEvent}
                        isDragging={isDragging}
                        isResizing={isResizing}
                        draggedEvent={draggedEvent}
                        pendingDrag={pendingDrag}
                        pendingResize={pendingResize}
                        onMouseDown={handleMouseDown}
                        onResizeMouseDown={handleResizeMouseDown}
                        onDragEnd={handleDragEnd}
                        onResizeEnd={handleResizeEnd}
                        dragStartY={dragStartY}
                        dragStartX={dragStartX}
                        dragThreshold={DRAG_THRESHOLD}
                        activateDrag={activateDrag}
                        activateResize={activateResize}
                        cancelPending={cancelPending}
                        resizeEdge={resizeEdge}
                        slotSelection={slotSelection}
                        onSlotSelectionStart={handleSlotSelectionStart}
                        onSlotSelectionMove={handleSlotSelectionMove}
                        onSlotSelectionEnd={handleSlotSelectionEnd}
                        dragMousePos={dragMousePos}
                        setDragMousePos={setDragMousePos}
                        isModalOpen={showEventModal}
                    />
                )}
            </div>

            {/* Event Modal */}
            {showEventModal && (
                <EventModal
                    event={selectedEvent}
                    mode={modalMode}
                    language={language}
                    t={t}
                    onClose={() => { setShowEventModal(false); setSlotSelection(null); }}
                    onSave={handleSaveEvent}
                    onDelete={handleDeleteEvent}
                    onSwitchToEdit={handleSwitchToEdit}
                    slotSelection={slotSelection}
                    setSlotSelection={setSlotSelection}
                />
            )}

            {/* Availability Configuration Modal */}
            <AvailabilityModal
                isOpen={showAvailabilityModal}
                onClose={() => setShowAvailabilityModal(false)}
                onSave={handleSaveAvailability}
                existingEvents={events}
            />

            {/* Calendar Settings Modal - DISABLED
            <CalendarSettings
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                peopleId={userId}
                onConnectionChange={handleGoogleConnectionChange}
            />
            */}
        </div>
    );
};

// Month View Component
const MonthView = ({ currentDate, events, language, t, onEventClick, onEventDoubleClick, onDateClick }) => {
    const days = useMemo(() => {
        const result = [];
        const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

        // Start from Monday of the week containing the first day
        const start = new Date(firstDay);
        start.setDate(start.getDate() - ((start.getDay() + 6) % 7));

        // End on Sunday of the week containing the last day
        const end = new Date(lastDay);
        end.setDate(end.getDate() + (7 - end.getDay()) % 7);

        const current = new Date(start);
        while (current <= end) {
            result.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }

        return result;
    }, [currentDate]);

    const getEventsForDay = (date) => {
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);

        return events.filter(e => {
            const eventStart = new Date(e.start);
            const eventEnd = new Date(e.end);
            // Event overlaps with this day if it starts before day ends AND ends after day starts
            return eventStart <= dayEnd && eventEnd >= dayStart;
        });
    };

    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const isCurrentMonth = (date) => {
        return date.getMonth() === currentDate.getMonth();
    };

    const dayNames = [
        t.calendar?.monday || 'Mon',
        t.calendar?.tuesday || 'Tue',
        t.calendar?.wednesday || 'Wed',
        t.calendar?.thursday || 'Thu',
        t.calendar?.friday || 'Fri',
        t.calendar?.saturday || 'Sat',
        t.calendar?.sunday || 'Sun'
    ];

    return (
        <div className="h-full flex flex-col">
            {/* Day headers */}
            <div className="grid grid-cols-7 border-b border-gray-200">
                {dayNames.map((day) => (
                    <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                        {day}
                    </div>
                ))}
            </div>

            {/* Days grid */}
            <div className="flex-1 grid grid-cols-7 auto-rows-fr">
                {days.map((date, idx) => {
                    const dayEvents = getEventsForDay(date);
                    return (
                        <div
                            key={idx}
                            onClick={() => onDateClick(date)}
                            className={`border-b border-r border-gray-100 p-1 min-h-[80px] cursor-pointer hover:bg-gray-50 transition-colors ${
                                !isCurrentMonth(date) ? 'bg-gray-50' : ''
                            }`}
                        >
                            <div className={`text-sm font-medium mb-1 ${
                                isToday(date)
                                    ? 'bg-linkoo-purple text-white w-6 h-6 rounded-full flex items-center justify-center'
                                    : isCurrentMonth(date) ? 'text-gray-900' : 'text-gray-400'
                            }`}>
                                {date.getDate()}
                            </div>
                            <div className="space-y-0.5 overflow-hidden">
                                {dayEvents.slice(0, 3).map((event) => (
                                    <div
                                        key={event.id}
                                        onClick={(e) => { e.stopPropagation(); onEventClick(event); }}
                                        onDoubleClick={(e) => { e.stopPropagation(); onEventDoubleClick(event); }}
                                        className="text-xs px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80"
                                        style={{ backgroundColor: event.color || '#3b82f6', color: 'white' }}
                                    >
                                        {event.title}
                                    </div>
                                ))}
                                {dayEvents.length > 3 && (
                                    <div className="text-xs text-gray-500 px-1">
                                        +{dayEvents.length - 3} {t.calendar?.others || 'more'}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Week View Component
const WeekView = ({
    currentDate, events, language, t, onEventClick, onEventDoubleClick, onSlotClick,
    isDragging, isResizing, draggedEvent, pendingDrag, pendingResize,
    onMouseDown, onResizeMouseDown, onDragEnd, onResizeEnd,
    dragStartY, dragStartX, dragThreshold, activateDrag, activateResize, cancelPending,
    resetDragState, resizeEdge,
    slotSelection, onSlotSelectionStart, onSlotSelectionMove, onSlotSelectionEnd,
    dragMousePos, setDragMousePos, isModalOpen
}) => {
    const hours = Array.from({ length: 24 }, (_, i) => i); // 0:00 - 23:00
    const gridRef = useRef(null);
    const [dragPreview, setDragPreview] = useState(null);
    const dragPreviewRef = useRef(null);
    const isSelectingSlotRef = useRef(false);
    const isModalOpenRef = useRef(isModalOpen);

    useEffect(() => {
        isModalOpenRef.current = isModalOpen;
    }, [isModalOpen]);

    // Keep ref in sync with state
    useEffect(() => {
        dragPreviewRef.current = dragPreview;
    }, [dragPreview]);

    const weekDays = useMemo(() => {
        const result = [];
        const start = new Date(currentDate);
        start.setDate(start.getDate() - ((start.getDay() + 6) % 7)); // Start Monday

        for (let i = 0; i < 7; i++) {
            const day = new Date(start);
            day.setDate(start.getDate() + i);
            result.push(day);
        }
        return result;
    }, [currentDate]);

    const weekDaysRef = useRef(weekDays);
    useEffect(() => {
        weekDaysRef.current = weekDays;
    }, [weekDays]);

    const getEventsForDay = (date) => {
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);

        return events.filter(e => {
            const eventStart = new Date(e.start);
            const eventEnd = new Date(e.end);
            // Event overlaps with this day if it starts before day ends AND ends after day starts
            return eventStart <= dayEnd && eventEnd >= dayStart;
        }).map(e => {
            // For multi-day events, adjust the display times for this specific day
            const eventStart = new Date(e.start);
            const eventEnd = new Date(e.end);
            const isMultiDay = eventStart.toDateString() !== eventEnd.toDateString();

            let displayStart = eventStart;
            let displayEnd = eventEnd;

            if (isMultiDay) {
                // If event starts before this day, show from 0:00
                if (eventStart < dayStart) {
                    displayStart = new Date(date);
                    displayStart.setHours(0, 0, 0, 0);
                }
                // If event ends after this day, show until 23:59
                if (eventEnd > dayEnd) {
                    displayEnd = new Date(date);
                    displayEnd.setHours(23, 59, 59, 999);
                }
            }

            return {
                ...e,
                displayStart,
                displayEnd,
                isMultiDay,
                isFirstDay: eventStart.toDateString() === date.toDateString(),
                isLastDay: eventEnd.toDateString() === date.toDateString()
            };
        });
    };

    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const getEventPosition = (event) => {
        // Use displayStart/displayEnd for multi-day events, otherwise use start/end
        const start = event.displayStart || new Date(event.start);
        const end = event.displayEnd || new Date(event.end);
        const startHour = start.getHours() + start.getMinutes() / 60;
        const endHour = end.getHours() + end.getMinutes() / 60;
        const top = (startHour / 24) * 100;
        const height = ((endHour - startHour) / 24) * 100;
        return { top: `${Math.max(top, 0)}%`, height: `${Math.max(height, 4)}%` };
    };

    const dayNames = [
        t.calendar?.monday || 'Mon',
        t.calendar?.tuesday || 'Tue',
        t.calendar?.wednesday || 'Wed',
        t.calendar?.thursday || 'Thu',
        t.calendar?.friday || 'Fri',
        t.calendar?.saturday || 'Sat',
        t.calendar?.sunday || 'Sun'
    ];

    // Calculate hour from Y position
    const getHourFromY = (clientY) => {
        if (!gridRef.current) return 6;
        const rect = gridRef.current.getBoundingClientRect();
        const relativeY = clientY - rect.top;
        const totalHeight = rect.height;
        const hourOffset = (relativeY / totalHeight) * 16; // 16 hours shown
        return Math.max(6, Math.min(21, Math.floor(6 + hourOffset)));
    };

    // Calculate minutes from Y position (for 15-minute slots)
    const getMinutesFromY = (clientY) => {
        if (!gridRef.current) return 0;
        const rect = gridRef.current.getBoundingClientRect();
        const relativeY = clientY - rect.top;
        const totalHeight = rect.height;
        const minuteOffset = (relativeY / totalHeight) * 24 * 60; // 24 hours * 60 minutes
        // Round to nearest 15 minutes
        const rounded = Math.round(minuteOffset / 15) * 15;
        return Math.max(0, Math.min(23 * 60 + 45, rounded));
    };

    // Calculate day index from X position
    const getDayFromX = (clientX) => {
        if (!gridRef.current) return 0;
        const rect = gridRef.current.getBoundingClientRect();
        const timeColumnWidth = 64; // w-16 = 64px
        const relativeX = clientX - rect.left - timeColumnWidth;
        const dayWidth = (rect.width - timeColumnWidth) / 7;
        return Math.max(0, Math.min(6, Math.floor(relativeX / dayWidth)));
    };

    // Calculate the position for the dragged event block
    const getDraggedBlockPosition = (clientX, clientY) => {
        if (!gridRef.current) return null;
        const rect = gridRef.current.getBoundingClientRect();
        const timeColumnWidth = 64;
        const dayWidth = (rect.width - timeColumnWidth) / 7;
        const dayIdx = getDayFromX(clientX);

        // Calculate left position (relative to grid)
        const left = timeColumnWidth + dayIdx * dayWidth;

        // Calculate top position based on mouse Y (snapped to quarter hour)
        const minutes = getMinutesFromY(clientY);
        const top = (minutes / (24 * 60)) * rect.height;

        return { left, top, dayIdx, minutes, dayWidth, gridTop: rect.top, gridLeft: rect.left };
    };

    // Store callbacks in refs to avoid stale closures
    const onDragEndRef = useRef(onDragEnd);
    const onResizeEndRef = useRef(onResizeEnd);
    const onSlotSelectionMoveRef = useRef(onSlotSelectionMove);
    const onSlotSelectionEndRef = useRef(onSlotSelectionEnd);
    const activateDragRef = useRef(activateDrag);
    const activateResizeRef = useRef(activateResize);
    const cancelPendingRef = useRef(cancelPending);
    const setDragMousePosRef = useRef(setDragMousePos);
    const onEventClickRef = useRef(onEventClick);
    useEffect(() => {
        onDragEndRef.current = onDragEnd;
        onResizeEndRef.current = onResizeEnd;
        onSlotSelectionMoveRef.current = onSlotSelectionMove;
        onSlotSelectionEndRef.current = onSlotSelectionEnd;
        activateDragRef.current = activateDrag;
        activateResizeRef.current = activateResize;
        cancelPendingRef.current = cancelPending;
        setDragMousePosRef.current = setDragMousePos;
        onEventClickRef.current = onEventClick;
    }, [onDragEnd, onResizeEnd, onSlotSelectionMove, onSlotSelectionEnd, activateDrag, activateResize, cancelPending, setDragMousePos, onEventClick]);

    // Pending drag/resize - detect threshold before activating
    useEffect(() => {
        if (!pendingDrag && !pendingResize) return;

        const handleMouseMove = (e) => {
            const deltaX = Math.abs(e.clientX - dragStartX);
            const deltaY = Math.abs(e.clientY - dragStartY);
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            if (distance >= dragThreshold) {
                if (pendingDrag) {
                    activateDragRef.current();
                    // Set initial mouse position when drag starts
                    setDragMousePosRef.current({ x: e.clientX, y: e.clientY });
                } else if (pendingResize) {
                    activateResizeRef.current();
                }
            }
        };

        const handleMouseUp = () => {
            // Mouse released before threshold - this is a simple click
            if (pendingDrag && pendingDrag.event) {
                // Open view modal on simple click
                onEventClickRef.current(pendingDrag.event);
            }
            cancelPendingRef.current();
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [pendingDrag, pendingResize, dragStartX, dragStartY, dragThreshold]);

    // Slot selection mouse handlers (WeekView - supports multi-day)
    useEffect(() => {
        if (!slotSelection) {
            isSelectingSlotRef.current = false;
            return;
        }
        // Don't handle mouse events if modal is open
        if (isModalOpenRef.current) {
            return;
        }
        isSelectingSlotRef.current = true;

        const handleMouseMove = (e) => {
            // Ignore if modal is open
            if (isModalOpenRef.current) return;
            const minutes = getMinutesFromY(e.clientY);
            const dayIdx = getDayFromX(e.clientX);
            const date = weekDaysRef.current[dayIdx];
            onSlotSelectionMoveRef.current(date, minutes);
        };

        const handleMouseUp = () => {
            // Ignore if modal is open
            if (isModalOpenRef.current) return;
            isSelectingSlotRef.current = false;
            onSlotSelectionEndRef.current();
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [slotSelection]);

    // Snap to quarter hour helper
    const snapToQuarter = (minutes) => Math.round(minutes / 15) * 15;

    // Add/remove global mouse listeners for drag/resize
    useEffect(() => {
        if (!isDragging && !isResizing) return;

        const handleMouseMove = (e) => {
            const dayIdx = getDayFromX(e.clientX);
            // Get minutes and snap to quarter hour
            const minutes = getMinutesFromY(e.clientY); // Already snapped in getMinutesFromY

            if (isDragging) {
                const preview = { dayIdx, minutes };
                setDragPreview(preview);
                dragPreviewRef.current = preview;
                // Update mouse position for visual drag
                setDragMousePosRef.current({ x: e.clientX, y: e.clientY });
            } else if (isResizing && dragStartY !== null) {
                const deltaY = e.clientY - dragStartY;
                // Calculate minutes delta and snap to quarter hour
                const rawMinutesDelta = Math.round((deltaY / 48) * 60); // 48px = 1 hour slot
                const minutesDelta = snapToQuarter(rawMinutesDelta);
                const preview = { minutesDelta };
                setDragPreview(preview);
                dragPreviewRef.current = preview;
            }
        };

        const handleMouseUp = async () => {
            const preview = dragPreviewRef.current;

            if (isDragging && preview && preview.dayIdx !== undefined) {
                const targetDate = weekDaysRef.current[preview.dayIdx];
                try {
                    await onDragEndRef.current(targetDate, preview.minutes);
                } catch (err) {
                    console.error('onDragEnd error:', err);
                }
            } else if (isResizing && preview) {
                try {
                    await onResizeEndRef.current(preview.minutesDelta || 0);
                } catch (err) {
                    console.error('onResizeEnd error:', err);
                }
            }
            setDragPreview(null);
            dragPreviewRef.current = null;
            setDragMousePosRef.current(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing, dragStartY]);

    return (
        <div className="h-full flex flex-col">
            {/* Day headers */}
            <div className="flex border-b border-gray-200">
                <div className="w-16 flex-shrink-0" /> {/* Time column spacer */}
                {weekDays.map((date, idx) => (
                    <div
                        key={idx}
                        className={`flex-1 p-2 text-center border-l border-gray-100 ${
                            isToday(date) ? 'bg-blue-50' : ''
                        }`}
                    >
                        <div className="text-xs text-gray-500">{dayNames[idx]}</div>
                        <div className={`text-lg font-semibold ${
                            isToday(date)
                                ? 'bg-linkoo-purple text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto'
                                : ''
                        }`}>
                            {date.getDate()}
                        </div>
                    </div>
                ))}
            </div>

            {/* Time grid */}
            <div className="flex-1 overflow-auto">
                <div ref={gridRef} className="flex min-h-[800px]">
                    {/* Time column */}
                    <div className="w-16 flex-shrink-0">
                        {hours.map((hour) => (
                            <div key={hour} className="h-12 border-b border-gray-100 pr-2 text-right">
                                <span className="text-xs text-gray-400">
                                    {hour.toString().padStart(2, '0')}:00
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Day columns */}
                    {weekDays.map((date, dayIdx) => {
                        const dayEvents = getEventsForDay(date);
                        const isDropTarget = isDragging && dragPreview?.dayIdx === dayIdx;
                        // Check if this day is within the selection range (compare dates only, not times)
                        const isInSelectionRange = slotSelection && (() => {
                            const dayStr = date.toDateString();
                            const startStr = slotSelection.startDate.toDateString();
                            const endStr = slotSelection.endDate.toDateString();
                            if (startStr === endStr) {
                                return dayStr === startStr;
                            }
                            // Normalize dates to midnight for comparison
                            const dayMidnight = new Date(date);
                            dayMidnight.setHours(0, 0, 0, 0);
                            const startMidnight = new Date(slotSelection.startDate);
                            startMidnight.setHours(0, 0, 0, 0);
                            const endMidnight = new Date(slotSelection.endDate);
                            endMidnight.setHours(23, 59, 59, 999);
                            return dayMidnight >= startMidnight && dayMidnight <= endMidnight;
                        })();
                        return (
                            <div
                                key={dayIdx}
                                className={`flex-1 border-l border-gray-100 relative ${
                                    isToday(date) ? 'bg-blue-50/30' : ''
                                } ${isDropTarget ? 'bg-blue-100/50' : ''}`}
                            >
                                {/* Hour slots - divided into 4 quarters */}
                                {hours.map((hour) => {
                                    return (
                                        <div
                                            key={hour}
                                            className="h-12 border-b border-gray-100 relative"
                                        >
                                            {/* 4 quarter-hour slots */}
                                            {[0, 15, 30, 45].map((quarterMinutes) => {
                                                const slotMinutes = hour * 60 + quarterMinutes;
                                                return (
                                                    <div
                                                        key={quarterMinutes}
                                                        onMouseDown={(e) => {
                                                            if (isDragging || isResizing) return;
                                                            e.preventDefault();
                                                            onSlotSelectionStart(date, slotMinutes);
                                                        }}
                                                        className={`h-3 cursor-pointer transition-colors hover:bg-gray-100 ${quarterMinutes === 0 ? '' : 'border-t border-gray-50'}`}
                                                    />
                                                );
                                            })}
                                        </div>
                                    );
                                })}

                                {/* Events */}
                                {dayEvents.map((event) => {
                                    const pos = getEventPosition(event);
                                    const isBeingDragged = draggedEvent?.id === event.id && isDragging;
                                    const isBeingResized = draggedEvent?.id === event.id && isResizing;

                                    // Hide event at original position when being dragged
                                    if (isBeingDragged) {
                                        return (
                                            <div
                                                key={event.id}
                                                className="absolute left-0.5 right-0.5 px-1 py-0.5 rounded text-xs overflow-hidden border-2 border-dashed border-gray-300"
                                                style={{
                                                    top: pos.top,
                                                    height: pos.height,
                                                    backgroundColor: 'transparent',
                                                    minHeight: '20px',
                                                    zIndex: 5
                                                }}
                                            />
                                        );
                                    }

                                    // Adjust position during resize
                                    let adjustedTop = pos.top;
                                    let adjustedHeight = pos.height;
                                    if (isBeingResized && dragPreview?.minutesDelta) {
                                        const deltaPx = (dragPreview.minutesDelta / 60) * (100 / 24);
                                        if (resizeEdge === 'top') {
                                            adjustedTop = `${parseFloat(pos.top) + deltaPx}%`;
                                            adjustedHeight = `${parseFloat(pos.height) - deltaPx}%`;
                                        } else {
                                            adjustedHeight = `${parseFloat(pos.height) + deltaPx}%`;
                                        }
                                    }

                                    return (
                                        <div
                                            key={event.id}
                                            onDoubleClick={(e) => { e.stopPropagation(); if (!isDragging && !isResizing && !pendingDrag && !pendingResize) onEventDoubleClick(event); }}
                                            onMouseDown={(e) => onMouseDown(event, e, date)}
                                            className={`absolute left-0.5 right-0.5 px-1 py-0.5 rounded text-xs text-white overflow-hidden transition-all group select-none ${
                                                isBeingResized ? 'cursor-ns-resize' : 'cursor-grab hover:opacity-90'
                                            }`}
                                            style={{
                                                top: adjustedTop,
                                                height: adjustedHeight,
                                                backgroundColor: event.color || '#3b82f6',
                                                minHeight: '20px',
                                                zIndex: isBeingResized ? 50 : 10
                                            }}
                                        >
                                            {/* Top resize handle */}
                                            <div
                                                onMouseDown={(e) => { e.stopPropagation(); onResizeMouseDown(event, 'top', e); }}
                                                className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 bg-white/30 rounded-t"
                                            />

                                            <div className="font-medium truncate">{event.title}</div>
                                            {parseFloat(pos.height) > 4 && (() => {
                                                const startDate = new Date(event.start);
                                                const endDate = new Date(event.end);
                                                const isSameDay = startDate.toDateString() === endDate.toDateString();
                                                const startTime = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                                const endTime = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                                                if (isSameDay) {
                                                    return (
                                                        <div className="text-[10px] opacity-80">
                                                            {startTime} - {endTime}
                                                        </div>
                                                    );
                                                } else {
                                                    const startDateStr = startDate.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
                                                    const endDateStr = endDate.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
                                                    return (
                                                        <div className="text-[10px] opacity-80">
                                                            {startDateStr} {startTime} - {endDateStr} {endTime}
                                                        </div>
                                                    );
                                                }
                                            })()}

                                            {/* Bottom resize handle */}
                                            <div
                                                onMouseDown={(e) => { e.stopPropagation(); onResizeMouseDown(event, 'bottom', e); }}
                                                className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 bg-white/30 rounded-b"
                                            />
                                        </div>
                                    );
                                })}

                                {/* Slot selection overlay - unified block (supports multi-day) */}
                                {isInSelectionRange && slotSelection && (() => {
                                    const dayStr = date.toDateString();
                                    const startStr = slotSelection.startDate.toDateString();
                                    const endStr = slotSelection.endDate.toDateString();
                                    const isSameDay = startStr === endStr;
                                    const isFirstDay = dayStr === startStr;
                                    const isLastDay = dayStr === endStr;

                                    // Calculate top and height based on position in selection
                                    let topMinutes, bottomMinutes;
                                    if (isSameDay) {
                                        topMinutes = slotSelection.startMinutes;
                                        bottomMinutes = slotSelection.endMinutes;
                                    } else if (isFirstDay) {
                                        topMinutes = slotSelection.startMinutes;
                                        bottomMinutes = 24 * 60; // End of day
                                    } else if (isLastDay) {
                                        topMinutes = 0; // Start of day
                                        bottomMinutes = slotSelection.endMinutes;
                                    } else {
                                        // Middle day - full day
                                        topMinutes = 0;
                                        bottomMinutes = 24 * 60;
                                    }

                                    const topPercent = (topMinutes / (24 * 60)) * 100;
                                    const heightPercent = ((bottomMinutes - topMinutes) / (24 * 60)) * 100;

                                    // Show times only on first day
                                    const startHour = Math.floor(slotSelection.startMinutes / 60);
                                    const startMin = slotSelection.startMinutes % 60;
                                    const endHour = Math.floor(slotSelection.endMinutes / 60);
                                    const endMin = slotSelection.endMinutes % 60;

                                    return (
                                        <div
                                            className="absolute left-0.5 right-0.5 pointer-events-none"
                                            style={{
                                                top: `${topPercent}%`,
                                                height: `${heightPercent}%`,
                                                backgroundColor: 'rgba(139, 92, 246, 0.7)',
                                                minHeight: '12px',
                                                zIndex: 15,
                                                borderTopLeftRadius: isFirstDay ? '0.5rem' : 0,
                                                borderTopRightRadius: isFirstDay ? '0.5rem' : 0,
                                                borderBottomLeftRadius: isLastDay ? '0.5rem' : 0,
                                                borderBottomRightRadius: isLastDay ? '0.5rem' : 0,
                                            }}
                                        >
                                            {isFirstDay && (
                                                <div className="px-1 py-0.5 text-white text-xs font-medium">
                                                    {String(startHour).padStart(2, '0')}:{String(startMin).padStart(2, '0')} - {String(endHour).padStart(2, '0')}:{String(endMin).padStart(2, '0')}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Dragged event block - follows mouse position snapped to grid */}
            {isDragging && dragPreview && draggedEvent && dragMousePos && (() => {
                const blockPos = getDraggedBlockPosition(dragMousePos.x, dragMousePos.y);
                if (!blockPos) return null;

                // Calculate event height based on duration
                const eventStart = new Date(draggedEvent.start);
                const eventEnd = new Date(draggedEvent.end);
                const durationMinutes = (eventEnd - eventStart) / 60000;
                // Calculate height in pixels based on grid height (48px per hour)
                const heightPx = (durationMinutes / 60) * 48;

                return (
                    <div
                        className="fixed z-50 px-1 py-0.5 rounded text-xs text-white shadow-lg overflow-hidden cursor-grabbing"
                        style={{
                            backgroundColor: draggedEvent.color || '#3b82f6',
                            left: `${blockPos.gridLeft + blockPos.left + 2}px`,
                            top: `${blockPos.gridTop + blockPos.top}px`,
                            width: `${blockPos.dayWidth - 4}px`,
                            height: `${heightPx}px`,
                            minHeight: '20px',
                            opacity: 0.95,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            border: '2px solid rgba(255,255,255,0.5)'
                        }}
                    >
                        <div className="font-medium truncate">{draggedEvent.title}</div>
                        <div className="text-[10px] opacity-90">
                            {String(Math.floor(dragPreview.minutes / 60)).padStart(2, '0')}:{String(dragPreview.minutes % 60).padStart(2, '0')}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

// Day View Component
const DayView = ({
    currentDate, events, language, t, onEventClick, onEventDoubleClick, onSlotClick,
    isDragging, isResizing, draggedEvent, pendingDrag, pendingResize,
    onMouseDown, onResizeMouseDown, onDragEnd, onResizeEnd,
    dragStartY, dragStartX, dragThreshold, activateDrag, activateResize, cancelPending,
    resizeEdge,
    slotSelection, onSlotSelectionStart, onSlotSelectionMove, onSlotSelectionEnd,
    dragMousePos, setDragMousePos, isModalOpen
}) => {
    const hours = Array.from({ length: 24 }, (_, i) => i); // 0:00 - 23:00
    const gridRef = useRef(null);
    const [dragPreview, setDragPreview] = useState(null);
    const dragPreviewRef = useRef(null);
    const isSelectingSlotRef = useRef(false);
    const isModalOpenRef = useRef(isModalOpen);

    useEffect(() => {
        isModalOpenRef.current = isModalOpen;
    }, [isModalOpen]);

    // Keep ref in sync with state
    useEffect(() => {
        dragPreviewRef.current = dragPreview;
    }, [dragPreview]);

    const currentDateRef = useRef(currentDate);
    useEffect(() => {
        currentDateRef.current = currentDate;
    }, [currentDate]);

    const dayEvents = events.filter(e => {
        const eventDate = new Date(e.start).toDateString();
        return eventDate === currentDate.toDateString();
    });

    const calcEventPosition = (event) => {
        const start = new Date(event.start);
        const end = new Date(event.end);
        const startHour = start.getHours() + start.getMinutes() / 60;
        const endHour = end.getHours() + end.getMinutes() / 60;
        const top = (startHour / 24) * 100;
        const height = ((endHour - startHour) / 24) * 100;
        return { top: `${top}%`, height: `${Math.max(height, 2)}%` };
    };

    // Calculate hour from Y position
    const getHourFromY = (clientY) => {
        if (!gridRef.current) return 6;
        const rect = gridRef.current.getBoundingClientRect();
        const relativeY = clientY - rect.top;
        const totalHeight = rect.height;
        const hourOffset = (relativeY / totalHeight) * 16;
        return Math.max(6, Math.min(21, Math.floor(6 + hourOffset)));
    };

    // Calculate minutes from Y position (for 15-minute slots)
    const getMinutesFromY = (clientY) => {
        if (!gridRef.current) return 0;
        const rect = gridRef.current.getBoundingClientRect();
        const relativeY = clientY - rect.top;
        const totalHeight = rect.height;
        const minuteOffset = (relativeY / totalHeight) * 24 * 60; // 24 hours
        const rounded = Math.round(minuteOffset / 15) * 15;
        return Math.max(0, Math.min(23 * 60 + 45, rounded));
    };

    // Calculate the position for the dragged event block in DayView
    const getDraggedBlockPosition = (clientY) => {
        if (!gridRef.current) return null;
        const rect = gridRef.current.getBoundingClientRect();
        const timeColumnWidth = 80; // w-20 = 80px in DayView

        // Calculate top position based on mouse Y (snapped to quarter hour)
        const minutes = getMinutesFromY(clientY);
        const top = (minutes / (24 * 60)) * rect.height;

        return {
            left: timeColumnWidth,
            top,
            minutes,
            width: rect.width - timeColumnWidth - 8,
            gridTop: rect.top,
            gridLeft: rect.left
        };
    };

    // Store callbacks in refs to avoid stale closures
    const onDragEndRef = useRef(onDragEnd);
    const onResizeEndRef = useRef(onResizeEnd);
    const onSlotSelectionMoveRef = useRef(onSlotSelectionMove);
    const onSlotSelectionEndRef = useRef(onSlotSelectionEnd);
    const activateDragRef = useRef(activateDrag);
    const activateResizeRef = useRef(activateResize);
    const cancelPendingRef = useRef(cancelPending);
    const setDragMousePosRef = useRef(setDragMousePos);
    const onEventClickRef = useRef(onEventClick);
    useEffect(() => {
        onDragEndRef.current = onDragEnd;
        onResizeEndRef.current = onResizeEnd;
        onSlotSelectionMoveRef.current = onSlotSelectionMove;
        onSlotSelectionEndRef.current = onSlotSelectionEnd;
        activateDragRef.current = activateDrag;
        activateResizeRef.current = activateResize;
        cancelPendingRef.current = cancelPending;
        setDragMousePosRef.current = setDragMousePos;
        onEventClickRef.current = onEventClick;
    }, [onDragEnd, onResizeEnd, onSlotSelectionMove, onSlotSelectionEnd, activateDrag, activateResize, cancelPending, setDragMousePos, onEventClick]);

    // Pending drag/resize - detect threshold before activating
    useEffect(() => {
        if (!pendingDrag && !pendingResize) return;

        const handleMouseMove = (e) => {
            const deltaX = Math.abs(e.clientX - dragStartX);
            const deltaY = Math.abs(e.clientY - dragStartY);
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            if (distance >= dragThreshold) {
                if (pendingDrag) {
                    activateDragRef.current();
                    setDragMousePosRef.current({ x: e.clientX, y: e.clientY });
                } else if (pendingResize) {
                    activateResizeRef.current();
                }
            }
        };

        const handleMouseUp = () => {
            // Mouse released before threshold - this is a simple click
            if (pendingDrag && pendingDrag.event) {
                // Open view modal on simple click
                onEventClickRef.current(pendingDrag.event);
            }
            cancelPendingRef.current();
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [pendingDrag, pendingResize, dragStartX, dragStartY, dragThreshold]);

    // Slot selection mouse handlers (DayView - single day)
    useEffect(() => {
        if (!slotSelection) {
            isSelectingSlotRef.current = false;
            return;
        }
        // Don't handle mouse events if modal is open
        if (isModalOpenRef.current) {
            return;
        }
        isSelectingSlotRef.current = true;

        const handleMouseMove = (e) => {
            // Ignore if modal is open
            if (isModalOpenRef.current) return;
            const minutes = getMinutesFromY(e.clientY);
            // DayView is single day, pass currentDate
            onSlotSelectionMoveRef.current(currentDateRef.current, minutes);
        };

        const handleMouseUp = () => {
            // Ignore if modal is open
            if (isModalOpenRef.current) return;
            isSelectingSlotRef.current = false;
            onSlotSelectionEndRef.current();
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [slotSelection]);

    // Snap to quarter hour helper
    const snapToQuarter = (minutes) => Math.round(minutes / 15) * 15;

    // Add/remove global mouse listeners for drag/resize
    useEffect(() => {
        if (!isDragging && !isResizing) return;

        const handleMouseMove = (e) => {
            // Get minutes and snap to quarter hour
            const minutes = getMinutesFromY(e.clientY); // Already snapped in getMinutesFromY

            if (isDragging) {
                const preview = { minutes };
                setDragPreview(preview);
                dragPreviewRef.current = preview;
                // Update mouse position for visual drag
                setDragMousePosRef.current({ x: e.clientX, y: e.clientY });
            } else if (isResizing && dragStartY !== null) {
                const deltaY = e.clientY - dragStartY;
                // Calculate minutes delta and snap to quarter hour
                const rawMinutesDelta = Math.round((deltaY / 48) * 60);
                const minutesDelta = snapToQuarter(rawMinutesDelta);
                const preview = { minutesDelta };
                setDragPreview(preview);
                dragPreviewRef.current = preview;
            }
        };

        const handleMouseUp = async () => {
            const preview = dragPreviewRef.current;

            if (isDragging && preview && preview.minutes !== undefined) {
                try {
                    await onDragEndRef.current(currentDateRef.current, preview.minutes);
                } catch (err) {
                    console.error('DayView onDragEnd error:', err);
                }
            } else if (isResizing && preview) {
                try {
                    await onResizeEndRef.current(preview.minutesDelta || 0);
                } catch (err) {
                    console.error('DayView onResizeEnd error:', err);
                }
            }
            setDragPreview(null);
            dragPreviewRef.current = null;
            setDragMousePosRef.current(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing, dragStartY]);

    return (
        <div className="h-full overflow-auto">
            <div ref={gridRef} className="flex min-h-[800px]">
                {/* Time column */}
                <div className="w-20 flex-shrink-0">
                    {hours.map((hour) => (
                        <div key={hour} className="h-12 border-b border-gray-100 pr-2 text-right">
                            <span className="text-sm text-gray-400">
                                {hour.toString().padStart(2, '0')}:00
                            </span>
                        </div>
                    ))}
                </div>

                {/* Day column */}
                <div className="flex-1 relative">
                    {/* Hour slots - divided into 4 quarters */}
                    {hours.map((hour) => {
                        return (
                            <div
                                key={hour}
                                className="h-12 border-b border-l border-gray-100 relative"
                            >
                                {/* 4 quarter-hour slots */}
                                {[0, 15, 30, 45].map((quarterMinutes) => {
                                    const slotMinutes = hour * 60 + quarterMinutes;
                                    // Highlight the target quarter slot when dragging
                                    const isTargetSlot = isDragging && dragPreview?.minutes !== undefined &&
                                        dragPreview.minutes >= slotMinutes && dragPreview.minutes < slotMinutes + 15;
                                    return (
                                        <div
                                            key={quarterMinutes}
                                            onMouseDown={(e) => {
                                                if (isDragging || isResizing) return;
                                                e.preventDefault();
                                                onSlotSelectionStart(currentDate, slotMinutes);
                                            }}
                                            className={`h-3 cursor-pointer transition-colors ${
                                                isTargetSlot ? 'bg-linkoo-purple/30' : 'hover:bg-gray-100'
                                            } ${quarterMinutes === 0 ? '' : 'border-t border-gray-50'}`}
                                        />
                                    );
                                })}
                            </div>
                        );
                    })}

                    {/* Events */}
                    {dayEvents.map((event) => {
                        const pos = calcEventPosition(event);
                        const isBeingDragged = draggedEvent?.id === event.id && isDragging;
                        const isBeingResized = draggedEvent?.id === event.id && isResizing;

                        // Hide event at original position when being dragged
                        if (isBeingDragged) {
                            return (
                                <div
                                    key={event.id}
                                    className="absolute left-1 right-1 px-2 py-1 rounded overflow-hidden border-2 border-dashed border-gray-300"
                                    style={{
                                        top: pos.top,
                                        height: pos.height,
                                        backgroundColor: 'transparent',
                                        minHeight: '24px',
                                        zIndex: 5
                                    }}
                                />
                            );
                        }

                        // Adjust position during resize
                        let adjustedTop = pos.top;
                        let adjustedHeight = pos.height;
                        if (isBeingResized && dragPreview?.minutesDelta) {
                            const deltaPx = (dragPreview.minutesDelta / 60) * (100 / 24);
                            if (resizeEdge === 'top') {
                                adjustedTop = `${parseFloat(pos.top) + deltaPx}%`;
                                adjustedHeight = `${parseFloat(pos.height) - deltaPx}%`;
                            } else {
                                adjustedHeight = `${parseFloat(pos.height) + deltaPx}%`;
                            }
                        }

                        return (
                            <div
                                key={event.id}
                                onDoubleClick={(e) => { e.stopPropagation(); if (!isDragging && !isResizing && !pendingDrag && !pendingResize) onEventDoubleClick(event); }}
                                onMouseDown={(e) => onMouseDown(event, e, currentDate)}
                                className={`absolute left-1 right-1 px-2 py-1 rounded text-white overflow-hidden transition-all group select-none ${
                                    isBeingResized ? 'cursor-ns-resize' : 'cursor-grab hover:opacity-90'
                                }`}
                                style={{
                                    top: adjustedTop,
                                    height: adjustedHeight,
                                    backgroundColor: event.color || '#3b82f6',
                                    minHeight: '24px',
                                    zIndex: isBeingResized ? 50 : 10
                                }}
                            >
                                {/* Top resize handle */}
                                <div
                                    onMouseDown={(e) => { e.stopPropagation(); onResizeMouseDown(event, 'top', e); }}
                                    className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 bg-white/30 rounded-t"
                                />

                                <div className="font-medium">{event.title}</div>
                                {(() => {
                                    const startDate = new Date(event.start);
                                    const endDate = new Date(event.end);
                                    const isSameDay = startDate.toDateString() === endDate.toDateString();
                                    const startTime = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                    const endTime = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                                    if (isSameDay) {
                                        return (
                                            <div className="text-sm opacity-80">
                                                {startTime} - {endTime}
                                            </div>
                                        );
                                    } else {
                                        const startDateStr = startDate.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
                                        const endDateStr = endDate.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
                                        return (
                                            <div className="text-sm opacity-80">
                                                {startDateStr} {startTime} - {endDateStr} {endTime}
                                            </div>
                                        );
                                    }
                                })()}
                                {event.location && (
                                    <div className="text-sm opacity-80 flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {event.location}
                                    </div>
                                )}

                                {/* Bottom resize handle */}
                                <div
                                    onMouseDown={(e) => { e.stopPropagation(); onResizeMouseDown(event, 'bottom', e); }}
                                    className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 bg-white/30 rounded-b"
                                />
                            </div>
                        );
                    })}

                    {/* Slot selection overlay - unified block */}
                    {slotSelection && slotSelection.startDate.toDateString() === currentDate.toDateString() && (() => {
                        const topPercent = (slotSelection.startMinutes / (24 * 60)) * 100;
                        const heightPercent = ((slotSelection.endMinutes - slotSelection.startMinutes) / (24 * 60)) * 100;
                        const startHour = Math.floor(slotSelection.startMinutes / 60);
                        const startMin = slotSelection.startMinutes % 60;
                        const endHour = Math.floor(slotSelection.endMinutes / 60);
                        const endMin = slotSelection.endMinutes % 60;
                        return (
                            <div
                                className="absolute left-1 right-1 rounded-lg pointer-events-none"
                                style={{
                                    top: `${topPercent}%`,
                                    height: `${heightPercent}%`,
                                    backgroundColor: 'rgba(139, 92, 246, 0.7)',
                                    minHeight: '12px',
                                    zIndex: 15
                                }}
                            >
                                <div className="px-2 py-1 text-white text-sm font-medium">
                                    {String(startHour).padStart(2, '0')}:{String(startMin).padStart(2, '0')} - {String(endHour).padStart(2, '0')}:{String(endMin).padStart(2, '0')}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Dragged event block - follows mouse position snapped to grid */}
                    {isDragging && dragPreview && draggedEvent && dragMousePos && (() => {
                        const blockPos = getDraggedBlockPosition(dragMousePos.y);
                        if (!blockPos) return null;

                        // Calculate event height based on duration
                        const eventStart = new Date(draggedEvent.start);
                        const eventEnd = new Date(draggedEvent.end);
                        const durationMinutes = (eventEnd - eventStart) / 60000;
                        const heightPx = (durationMinutes / 60) * 48;

                        return (
                            <div
                                className="fixed pointer-events-none z-50 px-2 py-1 rounded text-white shadow-lg overflow-hidden"
                                style={{
                                    backgroundColor: draggedEvent.color || '#3b82f6',
                                    left: `${blockPos.gridLeft + blockPos.left + 4}px`,
                                    top: `${blockPos.gridTop + blockPos.top}px`,
                                    width: `${blockPos.width}px`,
                                    height: `${heightPx}px`,
                                    minHeight: '24px',
                                    opacity: 0.95,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                    border: '2px solid rgba(255,255,255,0.5)'
                                }}
                            >
                                <div className="font-medium">{draggedEvent.title}</div>
                                <div className="text-sm opacity-90">
                                    {String(Math.floor(dragPreview.minutes / 60)).padStart(2, '0')}:{String(dragPreview.minutes % 60).padStart(2, '0')}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
};

// Event Modal Component - Only create/edit modes (no view mode)
const EventModal = ({ event, mode, language, t, onClose, onSave, onDelete, onSwitchToEdit, slotSelection, setSlotSelection }) => {
    const [formData, setFormData] = useState(event || {});
    const [saving, setSaving] = useState(false);

    // Draggable modal state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [isDraggingModal, setIsDraggingModal] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const modalRef = useRef(null);

    // Handle modal drag
    const handleModalDragStart = (e) => {
        if (e.target.tagName === 'BUTTON' || e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingModal(true);
        const rect = modalRef.current?.getBoundingClientRect();
        if (rect) {
            setDragOffset({
                x: e.clientX - rect.left - rect.width / 2,
                y: e.clientY - rect.top - rect.height / 2
            });
        }
    };

    useEffect(() => {
        if (!isDraggingModal) return;

        const handleMouseMove = (e) => {
            e.stopPropagation();
            // Calculate new position centered on mouse minus offset
            setModalPosition({
                x: e.clientX - window.innerWidth / 2 - dragOffset.x,
                y: e.clientY - window.innerHeight / 2 - dragOffset.y
            });
        };

        const handleMouseUp = (e) => {
            e.stopPropagation();
            setIsDraggingModal(false);
        };

        window.addEventListener('mousemove', handleMouseMove, true);
        window.addEventListener('mouseup', handleMouseUp, true);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove, true);
            window.removeEventListener('mouseup', handleMouseUp, true);
        };
    }, [isDraggingModal, dragOffset]);

    // Update slotSelection when form dates change
    useEffect(() => {
        if (!setSlotSelection || !formData.start || !formData.end) return;

        const startDate = new Date(formData.start);
        const endDate = new Date(formData.end);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return;

        const startMinutes = startDate.getHours() * 60 + startDate.getMinutes();
        const endMinutes = endDate.getHours() * 60 + endDate.getMinutes();

        setSlotSelection({
            startDate: startDate,
            endDate: endDate,
            startMinutes: startMinutes,
            endMinutes: endMinutes
        });
    }, [formData.start, formData.end, setSlotSelection]);

    // Participants state
    const [participants, setParticipants] = useState([]);
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [existingGroups, setExistingGroups] = useState([]); // Groups already linked to the event
    const [participantSearch, setParticipantSearch] = useState('');
    const [searchResults, setSearchResults] = useState({ contacts: [], peoples: [], groups: [] });
    const [isSearching, setIsSearching] = useState(false);
    const [loadingParticipants, setLoadingParticipants] = useState(false);

    // Load existing participants and groups when editing/viewing
    useEffect(() => {
        if (event?.id && (mode === 'edit' || mode === 'view')) {
            loadParticipantsAndGroups(event.id);
        }
    }, [event?.id, mode]);

    const loadParticipantsAndGroups = async (agendaId) => {
        setLoadingParticipants(true);
        try {
            const [participantsData, groupsData] = await Promise.all([
                getAgendaParticipants(agendaId),
                getAgendaGroups(agendaId)
            ]);
            setParticipants(participantsData);
            setExistingGroups(groupsData);
        } catch (error) {
            console.error('Error loading participants and groups:', error);
        } finally {
            setLoadingParticipants(false);
        }
    };

    // Search for contacts when typing
    useEffect(() => {
        const searchContacts = async () => {
            if (participantSearch.length < 2) {
                setSearchResults({ contacts: [], peoples: [], groups: [] });
                return;
            }
            setIsSearching(true);
            try {
                const results = await searchParticipantsAndGroups(participantSearch);
                // Results is a flat array with type field, separate by type
                const existingIds = [...participants.map(p => p.peopleId), ...selectedParticipants.map(p => p.id)];
                const selectedGroupIds = selectedGroups.map(g => g.groupId);
                const existingGroupIds = existingGroups.map(g => g.groupId);
                const allGroupIds = [...selectedGroupIds, ...existingGroupIds];

                const contacts = (results || []).filter(r => r.type === 'contact' && !existingIds.includes(r.id));
                const peoples = (results || []).filter(r => r.type === 'people' && !existingIds.includes(r.id));
                const groups = (results || []).filter(r => r.type === 'group' && !allGroupIds.includes(r.groupId));

                setSearchResults({ contacts, peoples, groups });
            } catch (error) {
                console.error('Error searching contacts:', error);
            } finally {
                setIsSearching(false);
            }
        };

        const debounce = setTimeout(searchContacts, 300);
        return () => clearTimeout(debounce);
    }, [participantSearch, participants, selectedParticipants, selectedGroups, existingGroups]);

    const handleAddParticipant = (contact) => {
        setSelectedParticipants(prev => [...prev, {
            id: contact.id || contact.ID,
            firstName: contact.firstName || contact.FIRSTNAME || '',
            lastName: contact.lastName || contact.LASTNAME || '',
            email: contact.email || contact.EMAIL || '',
            type: contact.type || 'contact'
        }]);
        setParticipantSearch('');
        setSearchResults({ contacts: [], peoples: [], groups: [] });
    };

    const handleAddGroup = (group) => {
        setSelectedGroups(prev => [...prev, {
            groupId: group.groupId,
            groupName: group.groupName,
            memberCount: group.memberCount || 0
        }]);
        setParticipantSearch('');
        setSearchResults({ contacts: [], peoples: [], groups: [] });
    };

    const handleRemoveSelected = (peopleId) => {
        setSelectedParticipants(prev => prev.filter(p => p.id !== peopleId));
    };

    const handleRemoveSelectedGroup = (groupId) => {
        setSelectedGroups(prev => prev.filter(g => g.groupId !== groupId));
    };

    const handleRemoveExisting = async (peopleId) => {
        if (!event?.id) return;
        try {
            await removeAgendaParticipant(event.id, peopleId);
            setParticipants(prev => prev.filter(p => p.peopleId !== peopleId));
        } catch (error) {
            console.error('Error removing participant:', error);
        }
    };

    const handleRemoveExistingGroup = async (groupId) => {
        if (!event?.id) return;
        try {
            await removeAgendaGroup(event.id, groupId);
            setExistingGroups(prev => prev.filter(g => g.groupId !== groupId));
        } catch (error) {
            console.error('Error removing group:', error);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title?.trim()) {
            alert(t.calendar?.titleRequired || 'Title is required');
            return;
        }
        setSaving(true);
        try {
            // Save event and add participants (handled by parent component)
            await onSave(formData, selectedParticipants, selectedGroups);
        } finally {
            setSaving(false);
        }
    };

    // Format date for display in view mode
    const formatDateTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get event type label
    const getEventTypeLabel = (type) => {
        const eventType = EVENT_TYPES.find(t => t.value === type);
        if (eventType) {
            return language === 'fr' ? eventType.labelFr : eventType.labelEn;
        }
        return type || 'Event';
    };

    // View mode - read-only display
    if (mode === 'view') {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onMouseDown={(e) => e.stopPropagation()}>
                <div
                    ref={modalRef}
                    className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
                    style={{
                        transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
                        cursor: isDraggingModal ? 'grabbing' : 'default'
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    {/* Header with color bar - draggable */}
                    <div
                        className="h-2 rounded-t-xl"
                        style={{ backgroundColor: formData.color || '#3b82f6' }}
                    />
                    <div
                        className="flex items-center justify-between p-4 border-b border-gray-200 cursor-grab active:cursor-grabbing"
                        onMouseDown={handleModalDragStart}
                    >
                        <h3 className="text-lg font-semibold text-gray-800">
                            {formData.title}
                        </h3>
                        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Event details */}
                    <div className="p-4 space-y-4">
                        {/* Date/Time */}
                        <div className="flex items-start gap-3">
                            <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                            <div>
                                <div className="text-sm text-gray-800">{formatDateTime(formData.start)}</div>
                                <div className="text-sm text-gray-500">→ {formatDateTime(formData.end)}</div>
                            </div>
                        </div>

                        {/* Event Type */}
                        <div className="flex items-center gap-3">
                            <CalendarIcon className="w-5 h-5 text-gray-400" />
                            <span className="text-sm text-gray-800">{getEventTypeLabel(formData.eventType)}</span>
                        </div>

                        {/* Location */}
                        {formData.location && (
                            <div className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                                <span className="text-sm text-gray-800">{formData.location}</span>
                            </div>
                        )}

                        {/* Recurrence */}
                        {formData.recurrence && formData.recurrence !== 'none' && (
                            <div className="flex items-center gap-3">
                                <Repeat className="w-5 h-5 text-gray-400" />
                                <span className="text-sm text-gray-800">
                                    {formData.recurrence === 'daily' && (t.calendar?.recurrenceDaily || 'Tous les jours')}
                                    {formData.recurrence === 'weekly' && (t.calendar?.recurrenceWeekly || 'Toutes les semaines')}
                                    {formData.recurrence === 'biweekly' && (t.calendar?.recurrenceBiweekly || 'Toutes les 2 semaines')}
                                    {formData.recurrence === 'monthly' && (t.calendar?.recurrenceMonthly || 'Tous les mois')}
                                    {formData.recurrence === 'yearly' && (t.calendar?.recurrenceYearly || 'Tous les ans')}
                                    {formData.recurrence === 'weekdays' && (t.calendar?.recurrenceWeekdays || 'Tous les jours de la semaine')}
                                </span>
                            </div>
                        )}

                        {/* Description */}
                        {formData.description && (
                            <div className="pt-2 border-t border-gray-100">
                                <p className="text-sm text-gray-600 whitespace-pre-wrap">{formData.description}</p>
                            </div>
                        )}

                        {/* Private indicator */}
                        {formData.isPrivate && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <EyeOff className="w-4 h-4" />
                                <span>{t.calendar?.privateEvent || 'Private event'}</span>
                            </div>
                        )}

                        {/* Participants section - View mode with add functionality */}
                        <div className="pt-2 border-t border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                                <Users className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-700">
                                    {t.calendar?.participants || 'Participants'}
                                    {participants.length > 0 && ` (${participants.length})`}
                                </span>
                            </div>

                            {/* Existing and selected participants/groups badges */}
                            {(participants.length > 0 || existingGroups.length > 0 || selectedParticipants.length > 0 || selectedGroups.length > 0) && (
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {/* Existing participants */}
                                    {participants.map(participant => (
                                        <div
                                            key={`view-existing-${participant.peopleId}`}
                                            className="flex items-center gap-1.5 px-2 py-1 bg-violet-100 text-violet-800 rounded-full text-sm"
                                        >
                                            <span>{participant.firstName} {participant.lastName}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveExisting(participant.peopleId)}
                                                className="p-0.5 hover:bg-violet-200 rounded-full"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                    {/* Existing groups */}
                                    {existingGroups.map(group => (
                                        <div
                                            key={`view-existing-group-${group.groupId}`}
                                            className="flex items-center gap-1.5 px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                                        >
                                            <Users className="w-3 h-3" />
                                            <span>{group.groupName}</span>
                                            <span className="text-xs opacity-70">({group.memberCount})</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveExistingGroup(group.groupId)}
                                                className="p-0.5 hover:bg-green-200 rounded-full"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                    {/* New selected participants */}
                                    {selectedParticipants.map(participant => (
                                        <div
                                            key={`view-new-${participant.id}`}
                                            className="flex items-center gap-1.5 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                                        >
                                            <span>{participant.firstName} {participant.lastName}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSelected(participant.id)}
                                                className="p-0.5 hover:bg-blue-200 rounded-full"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                    {/* New selected groups */}
                                    {selectedGroups.map(group => (
                                        <div
                                            key={`view-group-${group.groupId}`}
                                            className="flex items-center gap-1.5 px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm"
                                        >
                                            <Users className="w-3 h-3" />
                                            <span>{group.groupName}</span>
                                            <span className="text-xs opacity-70">({group.memberCount})</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSelectedGroup(group.groupId)}
                                                className="p-0.5 hover:bg-emerald-200 rounded-full"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Search input */}
                            <div className="relative">
                                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                <input
                                    type="text"
                                    placeholder={t.calendar?.searchContacts || 'Rechercher des contacts...'}
                                    value={participantSearch}
                                    onChange={(e) => setParticipantSearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                {isSearching && (
                                    <RefreshCw className="w-4 h-4 text-gray-400 animate-spin absolute right-3 top-1/2 transform -translate-y-1/2" />
                                )}
                            </div>

                            {/* Search results dropdown */}
                            {(searchResults.contacts?.length > 0 || searchResults.peoples?.length > 0 || searchResults.groups?.length > 0) && (
                                <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {/* Contacts */}
                                    {searchResults.contacts?.map(contact => (
                                        <button
                                            key={`view-contact-${contact.id}`}
                                            type="button"
                                            onClick={() => handleAddParticipant({ ...contact, type: 'contact' })}
                                            className="w-full px-3 py-2 text-left hover:bg-violet-50 flex items-center gap-2 text-sm"
                                        >
                                            <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 text-xs font-medium">
                                                {(contact.firstName || '?')[0]}
                                            </div>
                                            <span>{contact.firstName} {contact.lastName}</span>
                                            <span className="text-xs text-gray-400 ml-auto">Contact</span>
                                        </button>
                                    ))}
                                    {/* Public peoples */}
                                    {searchResults.peoples?.map(people => (
                                        <button
                                            key={`view-people-${people.id}`}
                                            type="button"
                                            onClick={() => handleAddParticipant({ ...people, type: 'people' })}
                                            className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2 text-sm"
                                        >
                                            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-medium">
                                                {(people.firstName || '?')[0]}
                                            </div>
                                            <span>{people.firstName} {people.lastName}</span>
                                            <span className="text-xs text-gray-400 ml-auto">Public</span>
                                        </button>
                                    ))}
                                    {/* Groups */}
                                    {searchResults.groups?.map(group => (
                                        <button
                                            key={`view-group-result-${group.groupId}`}
                                            type="button"
                                            onClick={() => handleAddGroup(group)}
                                            className="w-full px-3 py-2 text-left hover:bg-green-50 flex items-center gap-2 text-sm"
                                        >
                                            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                                <Users className="w-3.5 h-3.5" />
                                            </div>
                                            <span>{group.groupName}</span>
                                            <span className="text-xs text-gray-400 ml-auto">{group.memberCount} membres</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Save participants button - only show when there are new additions */}
                            {(selectedParticipants.length > 0 || selectedGroups.length > 0) && (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        setSaving(true);
                                        try {
                                            // Save new participants directly
                                            for (const participant of selectedParticipants) {
                                                await addAgendaParticipant(event.id, participant.id, 'confirmed');
                                            }
                                            // Save new groups (backend will also add members as participants)
                                            for (const group of selectedGroups) {
                                                await addAgendaGroup(event.id, group.groupId);
                                            }
                                            // Reload participants and groups
                                            await loadParticipantsAndGroups(event.id);
                                            setSelectedParticipants([]);
                                            setSelectedGroups([]);
                                        } catch (error) {
                                            console.error('Error saving participants:', error);
                                        } finally {
                                            setSaving(false);
                                        }
                                    }}
                                    disabled={saving}
                                    className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 bg-linkoo-purple text-white rounded-lg hover:bg-linkoo-pink transition-colors disabled:opacity-50"
                                >
                                    {saving ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4" />
                                    )}
                                    {t.calendar?.saveParticipants || 'Enregistrer les participants'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 p-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            {t.common?.close || 'Fermer'}
                        </button>
                        <button
                            type="button"
                            onClick={onSwitchToEdit}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg transition-colors"
                            style={{
                                backgroundColor: formData.color || '#3b82f6',
                                filter: 'brightness(1)'
                            }}
                            onMouseEnter={(e) => e.target.style.filter = 'brightness(0.9)'}
                            onMouseLeave={(e) => e.target.style.filter = 'brightness(1)'}
                        >
                            <Edit3 className="w-4 h-4" />
                            {t.calendar?.edit || 'Modifier'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onMouseDown={(e) => e.stopPropagation()}>
            <div
                ref={modalRef}
                className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
                style={{
                    transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
                    cursor: isDraggingModal ? 'grabbing' : 'default'
                }}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {/* Header with color bar - draggable */}
                <div
                    className="h-2 rounded-t-xl"
                    style={{ backgroundColor: formData.color || '#3b82f6' }}
                />
                <div
                    className="flex items-center justify-between p-4 border-b border-gray-200 cursor-grab active:cursor-grabbing"
                    onMouseDown={handleModalDragStart}
                >
                    <h3 className="text-lg font-semibold text-gray-800">
                        {mode === 'create' ? (t.calendar?.createEvent || 'New Event') : (t.calendar?.editEvent || 'Edit Event')}
                    </h3>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Form content */}
                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                        {/* Title */}
                        <div>
                            <input
                                type="text"
                                placeholder={t.calendar?.eventTitle || 'Event title'}
                                value={formData.title || ''}
                                onChange={(e) => handleChange('title', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent text-lg"
                                required
                            />
                        </div>

                        {/* Date/Time */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">
                                    {t.calendar?.start || 'Start'}
                                </label>
                                <input
                                    type="datetime-local"
                                    value={formData.start || ''}
                                    onChange={(e) => handleChange('start', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">
                                    {t.calendar?.end || 'End'}
                                </label>
                                <input
                                    type="datetime-local"
                                    value={formData.end || ''}
                                    onChange={(e) => handleChange('end', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    required
                                />
                            </div>
                        </div>

                        {/* Event Type & Color */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">
                                    {t.calendar?.type || 'Type'}
                                </label>
                                <CustomDropdown
                                    value={formData.eventType || 'appointment'}
                                    onChange={(value) => {
                                        const type = EVENT_TYPES.find(t => t.value === value);
                                        handleChange('eventType', value);
                                        if (type) handleChange('color', type.color);
                                    }}
                                    options={EVENT_TYPES.filter(t => t.value !== 'event').map((type) => ({
                                        value: type.value,
                                        label: language === 'fr' ? type.labelFr : type.labelEn
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">
                                    {t.calendar?.color || 'Color'}
                                </label>
                                <input
                                    type="color"
                                    value={formData.color || '#3b82f6'}
                                    onChange={(e) => handleChange('color', e.target.value)}
                                    className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
                                />
                            </div>
                        </div>

                        {/* Location */}
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <MapPin className="w-4 h-4 text-gray-400" />
                                <label className="text-xs text-gray-500">
                                    {t.calendar?.location || 'Location'}
                                </label>
                            </div>
                            <input
                                type="text"
                                placeholder={t.calendar?.addLocation || 'Add location'}
                                value={formData.location || ''}
                                onChange={(e) => handleChange('location', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                        </div>

                        {/* Recurrence */}
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <Repeat className="w-4 h-4 text-gray-400" />
                                <label className="text-xs text-gray-500">
                                    {t.calendar?.recurrence || 'Récurrence'}
                                </label>
                            </div>
                            <select
                                value={formData.recurrence || 'none'}
                                onChange={(e) => handleChange('recurrence', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                            >
                                <option value="none">{t.calendar?.recurrenceNone || 'Ne se répète pas'}</option>
                                <option value="daily">{t.calendar?.recurrenceDaily || 'Tous les jours'}</option>
                                <option value="weekly">{t.calendar?.recurrenceWeekly || 'Toutes les semaines'}</option>
                                <option value="biweekly">{t.calendar?.recurrenceBiweekly || 'Toutes les 2 semaines'}</option>
                                <option value="monthly">{t.calendar?.recurrenceMonthly || 'Tous les mois'}</option>
                                <option value="yearly">{t.calendar?.recurrenceYearly || 'Tous les ans'}</option>
                                <option value="weekdays">{t.calendar?.recurrenceWeekdays || 'Tous les jours de la semaine (lun-ven)'}</option>
                            </select>
                            {formData.recurrence && formData.recurrence !== 'none' && (
                                <div className="mt-2">
                                    <label className="text-xs text-gray-500 mb-1 block">
                                        {t.calendar?.recurrenceEnd || 'Fin de récurrence'}
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.recurrenceEndDate || ''}
                                        onChange={(e) => handleChange('recurrenceEndDate', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                        placeholder={t.calendar?.recurrenceEndPlaceholder || 'Optionnel'}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        <div>
                            <textarea
                                placeholder={t.calendar?.description || 'Description'}
                                value={formData.description || ''}
                                onChange={(e) => handleChange('description', e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                        </div>

                        {/* Private toggle */}
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.isPrivate || false}
                                onChange={(e) => handleChange('isPrivate', e.target.checked)}
                                className="w-4 h-4 text-linkoo-purple rounded"
                            />
                            <span className="text-sm text-gray-600">
                                {formData.isPrivate ? <EyeOff className="w-4 h-4 inline mr-1" /> : <Eye className="w-4 h-4 inline mr-1" />}
                                {t.calendar?.privateEvent || 'Private event'}
                            </span>
                        </label>

                        {/* Participants section - Edit mode */}
                        <div className="border-t border-gray-100 pt-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Users className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-700">
                                    {t.calendar?.participants || 'Participants'}
                                </span>
                            </div>

                            {/* Selected participants and groups badges */}
                            {(participants.length > 0 || existingGroups.length > 0 || selectedParticipants.length > 0 || selectedGroups.length > 0) && (
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {/* Existing participants */}
                                    {participants.map(participant => (
                                        <div
                                            key={`existing-${participant.peopleId}`}
                                            className="flex items-center gap-1.5 px-2 py-1 bg-violet-100 text-violet-800 rounded-full text-sm"
                                        >
                                            <span>{participant.firstName} {participant.lastName}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveExisting(participant.peopleId)}
                                                className="p-0.5 hover:bg-violet-200 rounded-full"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                    {/* Existing groups */}
                                    {existingGroups.map(group => (
                                        <div
                                            key={`existing-group-${group.groupId}`}
                                            className="flex items-center gap-1.5 px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                                        >
                                            <Users className="w-3 h-3" />
                                            <span>{group.groupName}</span>
                                            <span className="text-xs opacity-70">({group.memberCount})</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveExistingGroup(group.groupId)}
                                                className="p-0.5 hover:bg-green-200 rounded-full"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                    {/* New selected participants */}
                                    {selectedParticipants.map(participant => (
                                        <div
                                            key={`new-${participant.id}`}
                                            className="flex items-center gap-1.5 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                                        >
                                            <span>{participant.firstName} {participant.lastName}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSelected(participant.id)}
                                                className="p-0.5 hover:bg-blue-200 rounded-full"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                    {/* New selected groups */}
                                    {selectedGroups.map(group => (
                                        <div
                                            key={`group-${group.groupId}`}
                                            className="flex items-center gap-1.5 px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm"
                                        >
                                            <Users className="w-3 h-3" />
                                            <span>{group.groupName}</span>
                                            <span className="text-xs opacity-70">({group.memberCount})</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSelectedGroup(group.groupId)}
                                                className="p-0.5 hover:bg-emerald-200 rounded-full"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Search input */}
                            <div className="relative">
                                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                <input
                                    type="text"
                                    placeholder={t.calendar?.searchContacts || 'Rechercher des contacts...'}
                                    value={participantSearch}
                                    onChange={(e) => setParticipantSearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                {isSearching && (
                                    <RefreshCw className="w-4 h-4 text-gray-400 animate-spin absolute right-3 top-1/2 transform -translate-y-1/2" />
                                )}
                            </div>

                            {/* Search results dropdown */}
                            {(searchResults.contacts?.length > 0 || searchResults.peoples?.length > 0 || searchResults.groups?.length > 0) && (
                                <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {/* Contacts */}
                                    {searchResults.contacts?.map(contact => (
                                        <button
                                            key={`contact-${contact.id}`}
                                            type="button"
                                            onClick={() => handleAddParticipant({ ...contact, type: 'contact' })}
                                            className="w-full px-3 py-2 text-left hover:bg-violet-50 flex items-center gap-2 text-sm"
                                        >
                                            <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 text-xs font-medium">
                                                {(contact.firstName || '?')[0]}
                                            </div>
                                            <span>{contact.firstName} {contact.lastName}</span>
                                            <span className="text-xs text-gray-400 ml-auto">Contact</span>
                                        </button>
                                    ))}
                                    {/* Public peoples */}
                                    {searchResults.peoples?.map(people => (
                                        <button
                                            key={`people-${people.id}`}
                                            type="button"
                                            onClick={() => handleAddParticipant({ ...people, type: 'people' })}
                                            className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2 text-sm"
                                        >
                                            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-medium">
                                                {(people.firstName || '?')[0]}
                                            </div>
                                            <span>{people.firstName} {people.lastName}</span>
                                            <span className="text-xs text-gray-400 ml-auto">Public</span>
                                        </button>
                                    ))}
                                    {/* Groups */}
                                    {searchResults.groups?.map(group => (
                                        <button
                                            key={`group-${group.groupId}`}
                                            type="button"
                                            onClick={() => handleAddGroup(group)}
                                            className="w-full px-3 py-2 text-left hover:bg-green-50 flex items-center gap-2 text-sm"
                                        >
                                            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                                <Users className="w-3.5 h-3.5" />
                                            </div>
                                            <span>{group.groupName}</span>
                                            <span className="text-xs text-gray-400 ml-auto">{group.memberCount} membres</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4">
                        {mode === 'edit' && (
                            <button
                                type="button"
                                onClick={() => onDelete(formData.eventUuid)}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            {t.common?.cancel || 'Cancel'}
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50"
                            style={{
                                backgroundColor: formData.color || '#3b82f6',
                                filter: 'brightness(1)'
                            }}
                            onMouseEnter={(e) => !saving && (e.target.style.filter = 'brightness(0.9)')}
                            onMouseLeave={(e) => e.target.style.filter = 'brightness(1)'}
                        >
                            {saving ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            {t.calendar?.save || 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Calendar;
